import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Plus, Trash2, Edit2, GripVertical, X, Check, Image } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("admin_token")}` });

function SortableImage({ item, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  return (
    <div ref={setNodeRef} style={style} className="bg-white rounded-xl border border-gray-200 overflow-hidden group" data-testid={`gallery-item-${item.id}`}>
      <div className="relative">
        <img src={item.src} alt={item.alt} className="w-full h-40 object-cover" onError={(e) => { e.target.src = "https://via.placeholder.com/300x160?text=Image"; }} />
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
          <button onClick={() => onEdit(item)} className="p-2 bg-white rounded-lg hover:bg-yellow-50 transition-colors" title="Edit">
            <Edit2 size={14} color="#0A2540" />
          </button>
          <button onClick={() => onDelete(item.id)} className="p-2 bg-white rounded-lg hover:bg-red-50 transition-colors" title="Delete">
            <Trash2 size={14} color="#ef4444" />
          </button>
        </div>
        <div {...attributes} {...listeners} className="absolute top-2 left-2 p-1.5 bg-black/40 rounded-lg cursor-grab active:cursor-grabbing">
          <GripVertical size={14} color="white" />
        </div>
      </div>
      <div className="p-3">
        <p className="text-xs font-semibold text-gray-700 truncate">{item.caption}</p>
        <p className="text-xs text-gray-400 truncate mt-0.5">{item.src}</p>
      </div>
    </div>
  );
}

const emptyForm = { src: "", alt: "", caption: "" };

export default function AdminGallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/gallery`);
      setItems(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditItem(null); setForm(emptyForm); setErrors({}); setShowModal(true); };
  const openEdit = (item) => { setEditItem(item); setForm({ src: item.src, alt: item.alt, caption: item.caption }); setErrors({}); setShowModal(true); };

  const validate = () => {
    const e = {};
    if (!form.src.trim()) e.src = "Image URL is required";
    if (!form.caption.trim()) e.caption = "Caption is required";
    return e;
  };

  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    try {
      const data = { src: form.src, alt: form.alt || form.caption, caption: form.caption };
      if (editItem) {
        const res = await axios.put(`${BACKEND_URL}/api/admin/gallery/${editItem.id}`, data, { headers: getHeaders() });
        setItems((prev) => prev.map((i) => (i.id === editItem.id ? res.data : i)));
      } else {
        const res = await axios.post(`${BACKEND_URL}/api/admin/gallery`, data, { headers: getHeaders() });
        setItems((prev) => [...prev, res.data]);
      }
      setShowModal(false);
    } catch { alert("Failed to save. Please try again."); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this image?")) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/gallery/${id}`, { headers: getHeaders() });
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch { alert("Failed to delete."); }
  };

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((i) => i.id === active.id);
    const newIdx = items.findIndex((i) => i.id === over.id);
    const reordered = arrayMove(items, oldIdx, newIdx);
    setItems(reordered);
    try {
      await axios.patch(`${BACKEND_URL}/api/admin/gallery/reorder`, { ids: reordered.map((i) => i.id) }, { headers: getHeaders() });
    } catch { load(); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Loading gallery...</div>;

  return (
    <div data-testid="admin-gallery-page" className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-extrabold" style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}>Gallery Management</h2>
          <p className="text-sm text-gray-400 mt-0.5">Drag to reorder. Changes save automatically.</p>
        </div>
        <button onClick={openAdd} data-testid="gallery-add-btn"
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-semibold transition-all hover:opacity-90"
          style={{ backgroundColor: "#0A2540" }}>
          <Plus size={16} /> Add Image
        </button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-gray-200">
          <Image size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-400">No images yet. Add your first gallery image.</p>
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={items.map((i) => i.id)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {items.map((item) => (
                <SortableImage key={item.id} item={item} onEdit={openEdit} onDelete={handleDelete} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-lg" style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}>
                {editItem ? "Edit Image" : "Add New Image"}
              </h3>
              <button onClick={() => setShowModal(false)}><X size={20} className="text-gray-400" /></button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wide">Image URL *</label>
                <input type="url" value={form.src} onChange={(e) => setForm({ ...form, src: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-900" />
                {errors.src && <p className="text-xs text-red-500 mt-1">{errors.src}</p>}
              </div>
              {form.src && (
                <img src={form.src} alt="Preview" className="w-full h-36 object-cover rounded-lg border" onError={(e) => { e.target.style.display = "none"; }} />
              )}
              <div>
                <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wide">Caption *</label>
                <input type="text" value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })}
                  placeholder="Describe the image..."
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-900" />
                {errors.caption && <p className="text-xs text-red-500 mt-1">{errors.caption}</p>}
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wide">Alt Text</label>
                <input type="text" value={form.alt} onChange={(e) => setForm({ ...form, alt: e.target.value })}
                  placeholder="Accessibility description"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-900" />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowModal(false)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving} data-testid="gallery-save-btn"
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-white text-sm font-semibold hover:opacity-90 disabled:opacity-60"
                style={{ backgroundColor: "#0A2540" }}>
                <Check size={14} /> {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
